import Protomux from 'protomux'
import c from 'compact-encoding'
import b4a from 'b4a'
import z32 from 'z32'
import {
  domainHash, sign, verify, usernameTopic, normalizeUsername, isValidUsername
} from '@pine/crypto'
import { Emitter } from './emitter.js'

const PROTOCOL = 'pine/username/1'
const frame = { preencode: c.buffer.preencode, encode: c.buffer.encode, decode: c.buffer.decode }

/** See pairing.js — an onmessage handler must never return a pending promise. */
function detach (fn) {
  return (...args) => { Promise.resolve().then(() => fn(...args)).catch(() => {}) }
}

const cardEncoding = {
  preencode (state, m) {
    c.fixed32.preencode(state, m.account)
    c.string.preencode(state, m.username)
    c.string.preencode(state, m.name)
    c.string.preencode(state, m.bio)
    c.uint.preencode(state, m.at)
    c.fixed64.preencode(state, m.signature)
  },
  encode (state, m) {
    c.fixed32.encode(state, m.account)
    c.string.encode(state, m.username)
    c.string.encode(state, m.name)
    c.string.encode(state, m.bio)
    c.uint.encode(state, m.at)
    c.fixed64.encode(state, m.signature)
  },
  decode (state) {
    return {
      account: c.fixed32.decode(state),
      username: c.string.decode(state),
      name: c.string.decode(state),
      bio: c.string.decode(state),
      at: c.uint.decode(state),
      signature: c.fixed64.decode(state)
    }
  }
}

function cardSignable (card) {
  return domainHash(
    'pine/username/card/v1',
    card.account,
    card.username,
    card.name,
    card.bio,
    String(card.at)
  )
}

function encodeCard (card) {
  const state = c.state()
  cardEncoding.preencode(state, card)
  state.buffer = b4a.allocUnsafe(state.end)
  cardEncoding.encode(state, card)
  return state.buffer
}

/** A card is only worth anything if the signature and the topic both check out. */
function decodeCard (buffer, expectedUsername) {
  let card
  try {
    card = cardEncoding.decode({ start: 0, end: buffer.byteLength, buffer })
  } catch {
    return null
  }

  if (normalizeUsername(card.username) !== expectedUsername) return null
  if (!verify(cardSignable(card), card.signature, card.account)) return null

  // A card is a live claim, not a certificate. One that is months old came
  // from somewhere other than the person answering right now.
  if (Math.abs(Date.now() - card.at) > 24 * 60 * 60 * 1000) return null

  return {
    account: z32.encode(card.account),
    accountKey: card.account,
    username: normalizeUsername(card.username),
    name: card.name,
    bio: card.bio,
    at: card.at
  }
}

/**
 * Announces this account's username so other people can find it, and looks
 * other names up.
 *
 * Publishing is just announcing on H(username) and handing a signed card to
 * anyone who connects. There is nothing to register and nothing to lose if
 * this device goes offline — the name simply stops resolving until it is back.
 */
export class UsernameDirectory extends Emitter {
  constructor (client) {
    super()
    this.client = client
    this.username = null
    this.topic = null
    this.channels = new Set()
    this._paired = new Set()
    this._detach = null
  }

  get card () {
    if (!this.username) return null
    const profile = this.client.identity.profile
    const card = {
      account: this.client.identity.publicKey,
      username: this.username,
      name: profile.name || '',
      bio: profile.bio || '',
      at: Date.now(),
      signature: null
    }
    card.signature = sign(cardSignable(card), this.client.identity.secretKey)
    return card
  }

  /** Start answering lookups for `name`, replacing any previous one. */
  async publish (name) {
    const username = normalizeUsername(name)
    if (username && !isValidUsername(username)) throw new Error('Invalid username')
    if (username === this.username) return

    await this.stop()
    if (!username) return

    this.username = username
    this.topic = usernameTopic(username)

    this._detach = this.client.transport.onConnection((conn) => this._attach(conn))
    for (const conn of this.client.transport.connections) this._attach(conn)
    this.client.transport.join(this.topic, { server: true, client: false })

    this.client.transport.announced(this.topic)
      .then(() => this.emit('published', username))
      .catch(() => {})
  }

  async stop () {
    if (this._detach) this._detach()
    this._detach = null

    if (this.topic) {
      for (const mux of this._paired) {
        try { mux.unpair({ protocol: PROTOCOL, id: this.topic }) } catch {}
      }
    }
    this._paired.clear()

    for (const ch of this.channels) {
      try { ch.close() } catch {}
    }
    this.channels.clear()
    if (this.topic) await this.client.transport.leave(this.topic).catch(() => {})
    this.topic = null
    this.username = null
  }

  /** Re-sign and re-send after the display name changes. */
  refresh () {
    const card = this.card
    if (!card) return
    const payload = encodeCard(card)
    for (const entry of this.channels) {
      try { entry.message.send(payload) } catch {}
    }
  }

  /**
   * Answer lookups on this connection.
   *
   * `pair` rather than a one-off `createChannel`: a searcher closes its
   * channel as soon as it has an answer, which closes ours with it. Creating
   * the channel once per connection would therefore answer the first lookup
   * from a peer and silently ignore every one after it, on a connection that
   * is still perfectly alive. `pair` fires again each time the remote opens
   * the channel.
   */
  _attach (stream) {
    if (!this.topic) return
    const mux = Protomux.from(stream)
    const key = { protocol: PROTOCOL, id: this.topic }

    mux.pair(key, () => this._respond(mux))
    this._paired.add(mux)

    // The remote may already have its channel open when we arrive.
    if (mux.opened(key)) this._respond(mux)
  }

  _respond (mux) {
    if (!this.topic) return
    const channel = mux.createChannel({
      protocol: PROTOCOL,
      id: this.topic,
      onclose: () => this.channels.delete(channel)
    })
    if (!channel) return

    const message = channel.addMessage({ encoding: frame })
    channel.message = message
    channel.open()
    this.channels.add(channel)

    const card = this.card
    if (card) {
      try { message.send(encodeCard(card)) } catch {}
    }
  }
}

/**
 * Resolve a username to the accounts currently claiming it.
 *
 * Returns every verified claim rather than a single answer, because nothing
 * in a serverless design can make a name exclusive. Callers show the list.
 */
export async function lookupUsername (client, name, { timeout = 6000 } = {}) {
  const username = normalizeUsername(name)
  if (!isValidUsername(username)) throw new Error('Invalid username')

  const topic = usernameTopic(username)
  const found = new Map()

  return new Promise((resolve) => {
    const channels = new Set()
    let settled = false

    const finish = () => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      clearTimeout(grace)
      clearInterval(retry)
      stopListening()
      for (const ch of channels) {
        try { ch.close() } catch {}
      }
      client.transport.leave(topic).catch(() => {})
      resolve([...found.values()].sort((a, b) => b.at - a.at))
    }

    const timer = setTimeout(finish, timeout)
    let grace = null

    const attach = (stream) => {
      const mux = Protomux.from(stream)
      const channel = mux.createChannel({
        protocol: PROTOCOL,
        id: topic,
        onclose: () => channels.delete(channel)
      })
      if (!channel) return

      channel.addMessage({
        encoding: frame,
        onmessage: detach((buffer) => {
          const card = decodeCard(buffer, username)
          if (!card) return
          if (card.account === client.identity.id) return // ourselves
          found.set(card.account, card)

          // A name usually has exactly one holder, so do not make everyone
          // wait out the full timeout for the common case — but leave enough
          // room for a second claimant to answer, because a collision the UI
          // never shows is worse than a slightly slower search.
          if (found.size === 1) {
            clearTimeout(grace)
            grace = setTimeout(finish, 1800)
          }
        })
      })
      channel.open()
      channels.add(channel)
    }

    const stopListening = client.transport.onConnection((conn) => attach(conn))
    for (const conn of client.transport.connections) attach(conn)
    client.transport.join(topic, { server: false, client: true })

    const retry = setInterval(() => {
      if (settled) return
      client.transport.refresh(topic)
      for (const conn of client.transport.connections) attach(conn)
    }, 1500)
    if (retry.unref) retry.unref()
  })
}

export { normalizeUsername, isValidUsername, usernameTopic }
